import React, { useState } from 'react';
import { Container, Row, Col, Button, Image, Modal } from 'react-bootstrap';
import GameRules from '../game/GameRules';
import GameSettings from '../game/GameSettings';
import BettingPanel from '../game/BettingPanel';
import ChallengerSelector from './ChallengerSelector';
import { GameSettings as GameSettingsType, Challenger } from '../../types';
import { useLanguage } from '../../i18n';
import '../../styles/LandingPage.css';
import diceIcon from '../../assets/dice-coin.png';
import steamBannerZh from '../../assets/steam-banner-zh.png';
import steamBannerEn from '../../assets/steam-banner-en.png';

interface LandingPageProps {
  onStartGame: () => void;
  onSaveSettings: (settings: GameSettingsType) => void;
  onPlaceBet: (amount: number) => void;
  initialSettings: GameSettingsType;
  playerGroschen: number;
  onOpenDiceSelector: () => void;
  onOpenDiceShop: () => void;
  onStartChallengerGame?: (challenger: Challenger) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({
  onStartGame,
  onSaveSettings,
  onPlaceBet,
  initialSettings,
  playerGroschen,
  onOpenDiceSelector,
  onOpenDiceShop,
  onStartChallengerGame
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [showBetting, setShowBetting] = useState(false);
  const [showChallengerSelector, setShowChallengerSelector] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [settings, setSettings] = useState<GameSettingsType>(initialSettings);
  const { t, language } = useLanguage();

  const handleStartClick = () => {
    setShowBetting(true);
  };

  const handlePlaceBet = (amount: number) => {
    onPlaceBet(amount);
    setShowBetting(false);
    onStartGame();
  };

  const handleSaveSettings = (newSettings: GameSettingsType) => {
    setSettings(newSettings);
    onSaveSettings(newSettings);
  };

  const handleChallengerClick = () => {
    setShowChallengerSelector(true);
  };

  const handleSelectChallenger = (challenger: Challenger) => {
    if (onStartChallengerGame) {
      onStartChallengerGame(challenger);
    }
    setShowChallengerSelector(false);
  };

  return (
    <Container className="landing-page">
      <Row className="justify-content-center">
        <Col md={8} className="text-center">
          <div className="landing-header">
            <div className="game-title-container">
              <Image
                src={diceIcon}
                alt={t('diceIcon')}
                className="dice-coin-icon"
                width={80}
                height={80}
              />
              <h1 className="game-title">{t('gameTitle')}</h1>
            </div>
          </div>

          <div className="groschen-display landing-groschen">
            <p>{t('playerBalance', { amount: playerGroschen })}</p>
          </div>

          {showChallengerSelector ? (
            <ChallengerSelector
              onSelectChallenger={handleSelectChallenger}
              onCancel={() => setShowChallengerSelector(false)}
              playerGroschen={playerGroschen}
            />
          ) : !showBetting ? (
            <div className="landing-buttons">
              <Button
                variant="kcd"
                className="kcd-button kcd-button-primary landing-button"
                onClick={handleStartClick}
              >
                {t('startGame')}
              </Button>

              <Button
                variant="kcd"
                className="kcd-button kcd-button-secondary landing-button"
                onClick={handleChallengerClick}
              >
                {t('challengers')}
              </Button>

              <Button
                variant="kcd"
                className="kcd-button landing-button"
                onClick={onOpenDiceSelector}
              >
                {t('diceCollection')}
              </Button>

              <Button
                variant="kcd"
                className="kcd-button landing-button"
                onClick={onOpenDiceShop}
              >
                {t('diceShop')}
              </Button>

              <Button
                variant="kcd"
                className="kcd-button landing-button"
                onClick={() => setShowSettings(true)}
              >
                {t('gameSettings')}
              </Button>

              <GameRules />
            </div>
          ) : (
            <div className="betting-container">
              <BettingPanel
                playerGroschen={playerGroschen}
                onPlaceBet={handlePlaceBet}
                computerDifficulty={settings.computerDifficulty}
              />
              <Button
                variant="kcd"
                className="kcd-button mt-3"
                onClick={() => setShowBetting(false)}
              >
                {t('back')}
              </Button>
            </div>
          )}
        </Col>
      </Row>

      <div className="steam-banner">
        <a
          href="https://store.steampowered.com/app/4587610/Farkle_Roguelike/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'steam_banner_click', {
                event_category: 'promotion',
                event_label: language,
              });
            }
          }}
        >
          <img
            src={language === 'zh' ? steamBannerZh : steamBannerEn}
            alt={language === 'zh' ? '在 Steam 上获取 Farkle Roguelike' : 'Get Farkle Roguelike on Steam'}
            className="steam-banner-img"
          />
        </a>
      </div>

      <div className="landing-footer">
        <span
          className="privacy-link"
          onClick={() => setShowPrivacy(true)}
        >
          {t('privacyPolicy')}
        </span>
      </div>

      <GameSettings
        show={showSettings}
        onHide={() => setShowSettings(false)}
        onSave={handleSaveSettings}
        initialSettings={settings}
      />

      <Modal show={showPrivacy} onHide={() => setShowPrivacy(false)} centered className="kcd-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t('privacyPolicy')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ whiteSpace: 'pre-line', fontSize: '0.9rem', lineHeight: '1.6' }}>
            {t('privacyPolicyContent')}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="kcd" className="kcd-button" onClick={() => setShowPrivacy(false)}>
            {t('close')}
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default LandingPage;
